import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Zap, CheckCircle, XCircle, MinusCircle, Loader2, FileText,
  ThumbsUp, ThumbsDown, CornerDownRight, Copy, Download, Braces, Check, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { CoachingEmailModal, CoachingSentBadge } from './CoachingEmailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  sent: 'bg-emerald-500/20 text-emerald-300',
  failed: 'bg-rose-500/20 text-rose-300',
  skipped: 'bg-amber-500/20 text-amber-300',
  pending: 'bg-white/5 text-white/40',
};

const MatchIcon = ({ matched }) => {
  if (matched === true) return <CheckCircle className="w-4 h-4 text-emerald-400" data-testid="trace-match-icon" />;
  if (matched === false) return <XCircle className="w-4 h-4 text-white/30" data-testid="trace-match-icon" />;
  return <MinusCircle className="w-4 h-4 text-white/30" data-testid="trace-match-icon" />;
};

// ── Extracted-fields presentation ───────────────────────────────────────────
// Managers need a copy-pasteable, human-readable render of the LLM output —
// not a JSON dump. We classify each field by shape and pick the right widget:
//   • bool          → Yes / No pill
//   • short string  → inline "Label: value"
//   • long string   → block with heading + paragraph
//   • array         → bulleted list
//   • number        → tabular number with optional score band
// Anything unrecognised falls back to a `JSON.stringify` inline.

const humanizeKey = (k) =>
  String(k || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Keys we know are long-form narrative — always render as their own block
// even if the LLM happens to return a short answer this time.
const NARRATIVE_KEYS = new Set([
  'manager_summary', 'evidence_snippet', 'coaching_issue', 'coaching_why',
  'coaching_fix', 'best_next_message', 'disc_evidence', 'reason_detected',
  'supporting_transcript_snippet', 'major_failure_reason',
]);

// Keys where an empty / null value is meaningful ("didn't happen") and we
// still want to show a "No" pill rather than hiding the row.
const BOOLEAN_TRISTATE_KEYS = new Set([
  'appointment_attempted', 'appointment_booked', 'emotional_goal_found',
  'price_requested', 'urgency_created', 'next_step_confirmed', 'major_failure',
]);

const isEmpty = (v) =>
  v === null
  || v === undefined
  || (typeof v === 'string' && v.trim() === '')
  || (Array.isArray(v) && v.length === 0);

const BoolPill = ({ value, dataTestId }) => {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 font-semibold" data-testid={dataTestId}>
        <CheckCircle className="w-3 h-3" />Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/20 font-semibold" data-testid={dataTestId}>
        <XCircle className="w-3 h-3" />No
      </span>
    );
  }
  return (
    <span className="text-[11px] text-white/30 italic" data-testid={dataTestId}>—</span>
  );
};

const ScorePill = ({ value }) => {
  const n = Number(value);
  if (Number.isNaN(n)) return <span className="text-white/80 text-sm tabular-nums">{String(value)}</span>;
  const band = n >= 80 ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20'
             : n >= 60 ? 'bg-teal-500/15 text-teal-300 border-teal-500/20'
             : n >= 40 ? 'bg-amber-500/15 text-amber-300 border-amber-500/20'
             : 'bg-rose-500/15 text-rose-300 border-rose-500/20';
  return (
    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border font-semibold tabular-nums ${band}`}>
      {n}
    </span>
  );
};

// Convert the extracted object into a plain-text formatted block suitable for
// pasting into Slack / email. Mirrors the visual grouping exactly.
const toPlainText = (data) => {
  if (!data || typeof data !== 'object') return '';
  const lines = [];
  Object.entries(data).forEach(([k, v]) => {
    if (isEmpty(v) && !BOOLEAN_TRISTATE_KEYS.has(k)) return;
    const label = humanizeKey(k);
    if (Array.isArray(v)) {
      lines.push(`${label}:`);
      v.forEach((item) => lines.push(`  • ${typeof item === 'string' ? item : JSON.stringify(item)}`));
      lines.push('');
      return;
    }
    if (typeof v === 'boolean') {
      lines.push(`${label}: ${v ? 'Yes' : 'No'}`);
      return;
    }
    if (v === null || v === undefined) {
      if (BOOLEAN_TRISTATE_KEYS.has(k)) lines.push(`${label}: —`);
      return;
    }
    if (typeof v === 'object') {
      lines.push(`${label}: ${JSON.stringify(v)}`);
      return;
    }
    const s = String(v);
    if (s.length > 80 || NARRATIVE_KEYS.has(k)) {
      lines.push(`${label}:`);
      lines.push(`  ${s}`);
      lines.push('');
    } else {
      lines.push(`${label}: ${s}`);
    }
  });
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

const KVRow = ({ k, v }) => {
  const label = humanizeKey(k);
  return (
    <div className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-white/5 last:border-b-0" data-testid={`extracted-kv-${k}`}>
      <span className="text-white/40 uppercase tracking-wider text-[10px]">{label}</span>
      <span className="text-white/85 text-right truncate max-w-[70%]">{v}</span>
    </div>
  );
};

const NarrativeBlock = ({ k, v }) => (
  <div className="space-y-1" data-testid={`extracted-narrative-${k}`}>
    <p className="text-white/40 uppercase tracking-wider text-[10px]">{humanizeKey(k)}</p>
    <blockquote className="text-white/80 text-sm leading-relaxed border-l-2 border-teal-400/40 pl-3 py-0.5 whitespace-pre-wrap">
      {String(v)}
    </blockquote>
  </div>
);

const ListBlock = ({ k, items }) => (
  <div className="space-y-1" data-testid={`extracted-list-${k}`}>
    <p className="text-white/40 uppercase tracking-wider text-[10px]">{humanizeKey(k)}</p>
    <ul className="space-y-1 text-sm text-white/80">
      {items.map((it, i) => (
        <li key={`${k}-${i}`} className="flex items-start gap-2">
          <span className="text-teal-400/70 mt-0.5">•</span>
          <span className="flex-1">{typeof it === 'string' ? it : JSON.stringify(it)}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ExtractedFields = ({ data, actionId }) => {
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);

  const plainText = useMemo(() => toPlainText(data), [data]);
  const rawJson = useMemo(() => JSON.stringify(data, null, 2), [data]);

  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return null;

  // Split fields into three groups by shape.
  const kvPairs = [];       // short scalars, numbers, booleans (bool tristate incl.)
  const narratives = [];    // long strings / narrative keys
  const lists = [];         // arrays

  Object.entries(data).forEach(([k, v]) => {
    if (isEmpty(v) && !BOOLEAN_TRISTATE_KEYS.has(k)) return;
    if (Array.isArray(v)) {
      lists.push([k, v]);
      return;
    }
    if (typeof v === 'string' && (v.length > 80 || NARRATIVE_KEYS.has(k))) {
      narratives.push([k, v]);
      return;
    }
    kvPairs.push([k, v]);
  });

  const renderKV = ([k, v]) => {
    if (typeof v === 'boolean' || (BOOLEAN_TRISTATE_KEYS.has(k) && (v === null || v === undefined))) {
      return <KVRow key={k} k={k} v={<BoolPill value={v ?? null} dataTestId={`extracted-bool-${k}`} />} />;
    }
    if ((k === 'final_score' || k === 'confidence_score' || k === 'score' || k.endsWith('_score')) && v !== null) {
      return <KVRow key={k} k={k} v={<ScorePill value={v} />} />;
    }
    if (v === null || v === undefined) {
      return <KVRow key={k} k={k} v={<span className="text-white/30 italic">—</span>} />;
    }
    return <KVRow key={k} k={k} v={<span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>} />;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      toast.success('Formatted log copied — paste into Slack, email, or your notes.');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed — your browser blocked clipboard access.');
    }
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([rawJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voicefabric-extracted-${actionId || 'log'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('download failed', err);
      toast.error('Download failed');
    }
  };

  return (
    <div className="mt-2 rounded-lg bg-white/5 border border-white/10 p-3 space-y-3" data-testid={`extracted-fields-${actionId || 'block'}`}>
      {kvPairs.length > 0 && (
        <div className="rounded-md bg-black/20 px-3 divide-y divide-white/5">
          {kvPairs.map(renderKV)}
        </div>
      )}
      {narratives.map(([k, v]) => <NarrativeBlock key={k} k={k} v={v} />)}
      {lists.map(([k, items]) => <ListBlock key={k} k={k} items={items} />)}

      <div className="flex items-center gap-2 pt-1 border-t border-white/5">
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 transition-colors"
          data-testid={`extracted-copy-${actionId || 'block'}`}
          title="Copy the formatted log to clipboard"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy formatted'}
        </button>
        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 transition-colors"
          data-testid={`extracted-download-${actionId || 'block'}`}
          title="Download the raw JSON payload"
        >
          <Download className="w-3 h-3" />Download raw
        </button>
        <button
          onClick={() => setShowRaw((s) => !s)}
          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
            showRaw ? 'bg-teal-500/15 text-teal-300 border-teal-500/30' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/70'
          }`}
          data-testid={`extracted-toggle-raw-${actionId || 'block'}`}
        >
          <Braces className="w-3 h-3" />{showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
        </button>
      </div>

      {showRaw && (
        <pre
          className="bg-black/40 rounded p-3 text-[11px] text-white/60 font-mono overflow-x-auto whitespace-pre-wrap max-h-80 overflow-y-auto"
          data-testid={`extracted-raw-json-${actionId || 'block'}`}
        >
          {rawJson}
        </pre>
      )}
    </div>
  );
};

// Provider responses are almost always API status blobs (delivery
// receipts, error payloads) — a KV/narrative split is overkill. Keep the
// simple JSON view for those.
const RawJsonBlock = ({ data }) => (
  <pre className="bg-black/30 rounded p-3 text-xs text-white/60 font-mono overflow-x-auto mt-2 whitespace-pre-wrap max-h-64 overflow-y-auto">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const ActionStep = ({ a, onFeedback, onCoachClick, depth = 0, isChild = false }) => {
  const matchLabel = a.matched === true ? 'MATCHED' : a.matched === false ? 'No match' : 'Evaluated';
  const matchColor = a.matched === true ? 'text-emerald-300' : 'text-white/40';
  const conf = a.confidence != null ? `${Math.round((a.confidence || 0) * 100)}%` : null;

  // Detect whether this action is coachable — we only show the "Coach this
  // call" button when the extracted payload has grading-style narrative
  // fields the composer can use.
  const extracted = a.extracted || a.extracted_data || {};
  const coachable = a.matched === true && !!(
    extracted.coaching_issue
    || extracted.coaching_fix
    || extracted.best_next_message
    || extracted.manager_summary
  );
  const alreadyCoached = !!a.coaching_email_sent_at;

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`trace-action-${a.id}`}>
      <div className="flex items-start gap-3">
        <MatchIcon matched={a.matched} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isChild && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-violet-300/80 bg-violet-500/10 px-1.5 py-0.5 rounded"
                title={`Chained from a parent rule (depth ${depth})`}
                data-testid={`trace-chain-badge-${a.id}`}
              >
                <CornerDownRight className="w-3 h-3" />
                Chained · depth {depth}
              </span>
            )}
            <span className="text-white text-sm font-semibold truncate">{a.rule_name}</span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${matchColor}`}>{matchLabel}</span>
            {conf && <span className="text-xs text-emerald-400/70">{conf}</span>}
            {a.rule_action_type && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 capitalize">
                {a.rule_action_type}
              </span>
            )}
            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${STATUS_PILL[a.dispatch_status] || STATUS_PILL.pending}`}>
              {a.dispatch_status || 'pending'}
            </span>
          </div>

          {a.reason && (
            <p className="text-white/50 text-xs italic mt-1.5">{a.reason}</p>
          )}

          {a.rule_trigger_prompt && (
            <details className="mt-2">
              <summary className="text-white/30 text-[11px] cursor-pointer hover:text-white/50">
                Trigger prompt
              </summary>
              <p className="text-white/40 text-xs font-mono bg-black/20 rounded p-2 mt-1 whitespace-pre-wrap">
                {a.rule_trigger_prompt}
              </p>
            </details>
          )}

          {(a.extracted || a.extracted_data) && (
            <div className="mt-3">
              <p className="text-white/40 text-[11px] uppercase tracking-wider mb-1.5 font-semibold">Extracted fields</p>
              <ExtractedFields data={a.extracted || a.extracted_data} actionId={a.id} />
            </div>
          )}

          {a.dispatch_error && (
            <p className="text-rose-400/70 text-xs font-mono mt-2 bg-rose-500/5 rounded p-2">
              {a.dispatch_error}
            </p>
          )}

          {a.provider_response && (
            <details className="mt-2">
              <summary className="text-white/30 text-[11px] cursor-pointer hover:text-white/50">
                Provider response
              </summary>
              <RawJsonBlock data={a.provider_response} />
            </details>
          )}

          <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/5 gap-2 flex-wrap">
            <span className="text-white/20 text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</span>
            <div className="flex items-center gap-2">
              {alreadyCoached && (
                <CoachingSentBadge at={a.coaching_email_sent_at} to={a.coaching_email_sent_to} />
              )}
              {coachable && !alreadyCoached && (
                <button
                  onClick={() => onCoachClick?.(a.id)}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-teal-500/15 hover:bg-teal-500/25 text-teal-300 border border-teal-500/30 font-semibold transition-colors"
                  data-testid={`coach-call-${a.id}`}
                  title="Compose a 1-click coaching email using this action's AI insights"
                >
                  <MessageSquare className="w-3 h-3" />
                  Coach this call
                </button>
              )}
              {onFeedback && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onFeedback(a.id, a.feedback === 'positive' ? 'clear' : 'positive')}
                    title={a.feedback === 'positive' ? 'Remove thumbs up' : 'Mark as correct'}
                    className={`p-1 rounded transition-colors ${a.feedback === 'positive' ? 'bg-emerald-500/20 text-emerald-300' : 'text-white/30 hover:text-emerald-400 hover:bg-white/5'}`}
                    data-testid={`trace-fb-up-${a.id}`}
                  >
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => onFeedback(a.id, a.feedback === 'negative' ? 'clear' : 'negative')}
                    title={a.feedback === 'negative' ? 'Remove thumbs down' : 'Mark as false positive'}
                    className={`p-1 rounded transition-colors ${a.feedback === 'negative' ? 'bg-rose-500/20 text-rose-300' : 'text-white/30 hover:text-rose-400 hover:bg-white/5'}`}
                    data-testid={`trace-fb-down-${a.id}`}
                  >
                    <ThumbsDown className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Coaching modal is rendered by the parent VoiceFabricTrace so it
              can share auth + reload state across the whole trace. */}
        </div>
      </div>
    </div>
  );
};

const ActionTree = ({ actions, onFeedback, onCoachClick }) => {
  // Build parent -> children map. Roots are actions with no chain_parent_id
  // OR whose parent isn't in the current set (defensive: parent might have
  // been pruned, e.g. by a future retention policy).
  const byId = new Map();
  actions.forEach(a => byId.set(a.id, a));
  const childrenByParent = new Map();
  const rootIds = [];
  actions.forEach(a => {
    const parentId = a.chain_parent_id;
    if (parentId && byId.has(parentId)) {
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(a);
    } else {
      rootIds.push(a.id);
    }
  });

  // Sort children chronologically (oldest first) so the chain reads top-down
  const tsKey = (a) => a.created_at || '';
  childrenByParent.forEach(arr => arr.sort((x, y) => tsKey(x).localeCompare(tsKey(y))));

  const renderNode = (action, depth) => {
    const children = childrenByParent.get(action.id) || [];
    return (
      <div key={action.id} className="space-y-2">
        <ActionStep
          a={action}
          onFeedback={onFeedback}
          onCoachClick={onCoachClick}
          depth={action.chain_depth || depth}
          isChild={depth > 0}
        />
        {children.length > 0 && (
          <div
            className="pl-5 ml-3 border-l-2 border-violet-400/30 space-y-2"
            data-testid={`trace-children-of-${action.id}`}
          >
            {children.map(c => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Roots in original (most-recent-first) order from the backend
  const roots = rootIds.map(id => byId.get(id));
  return (
    <div className="space-y-2" data-testid="trace-action-tree">
      {roots.map(r => renderNode(r, 0))}
    </div>
  );
};

export const VoiceFabricTrace = ({ callId, token, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachingActionId, setCoachingActionId] = useState(null);

  const load = () => axios.get(`${API}/calls/${callId}/voicefabric/trace`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(r => setData(r.data));

  useEffect(() => {
    setLoading(true);
    load()
      .catch((err) => {
        if (err?.response?.status !== 404) toast.error('Failed to load VoiceFabric trace');
      })
      .finally(() => setLoading(false));
    // `load` closes over callId/token — re-fetch only when those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, token]);

  const handleFeedback = async (actionId, feedback) => {
    try {
      await axios.post(`${API}/admin/voicefabric/actions/${actionId}/feedback`, { feedback }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Feedback saved');
      load();
    } catch {
      toast.error('Failed to save feedback');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-teal-400" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="voicefabric-trace">
      {!compact && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <Zap className="w-4 h-4 text-yellow-400" />
              VoiceFabric Trace
              <span className="ml-auto text-xs font-normal text-white/40">
                {data.matched_count} matched · {data.total_actions} evaluated · {data.all_enabled_rules_count} rules active
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.call_summary && (
              <div className="flex items-start gap-2 text-xs text-white/50">
                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-white/30" />
                <span className="italic">{data.call_summary}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {data.actions.length === 0 ? (
        <div className="text-center py-10 bg-white/5 border border-white/10 rounded-xl" data-testid="trace-empty-state">
          <Zap className="w-8 h-8 text-white/20 mx-auto mb-2" />
          <p className="text-white/40 text-sm">No VoiceFabric rules ran on this call yet.</p>
          {data.all_enabled_rules_count > 0 && (
            <p className="text-white/20 text-xs mt-1">
              ({data.all_enabled_rules_count} active rule{data.all_enabled_rules_count === 1 ? '' : 's'} for this domain — they may have been added after this call was graded)
            </p>
          )}
        </div>
      ) : (
        <ActionTree actions={data.actions} onFeedback={handleFeedback} onCoachClick={setCoachingActionId} />
      )}

      {coachingActionId && (
        <CoachingEmailModal
          actionId={coachingActionId}
          token={token}
          onClose={() => setCoachingActionId(null)}
          onSent={() => { load(); }}
        />
      )}
    </div>
  );
};
